import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";

import { Box, Typography, Switch, Divider } from "@mui/material";
import { childBoxStyleSpaceBetween } from "services/constants";
import { useForm } from "react-hook-form";
import { infoStyle, childBoxStyle } from "services/constants";
import { CustomAutoComplete } from "components/CustomComponents";
import { CustomTextBox } from "components/CustomComponents";
import { useFetch } from "services/hooks/useFetch";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { SalaryComponentTable } from "../SalaryTemplate/SalaryComponentTable";
import { useSalaryComponents } from "../SalaryTemplate/useSalaryComponents";
import { onlyNumberRegex } from "services/constants/patterns";
import { customGetAPI } from "services/utils/CustomAPI";
import { useSalaryRevise } from "./SalaryReviseContext";
import {
  updateCrossCategoryComponents,
  parsePercentageValue,
  buildSpecialAllowanceFormula,
  updatePercentageDisplay,
  formatComponents,
  SummaryData,
} from "./Components";

export const OverrideTemplate = forwardRef(
  ({ getCompanyId, salaryTemplates, revisionBasedOn, templateName }, ref) => {
    const { domain } = useSelector((state) => state.tokenReducer);
    const {
      setValue,
      handleSubmit,
      register,
      watch,
      trigger,
      formState: { errors },
    } = useForm({
      mode: "onBlur",
      defaultValues: {
        salary_cal_type: "",
        salary: "",
        salary_basis: "",
        new_template: "",
        is_employer_contribution_allowed: false,
        is_reimbursement_allowed: false,
        trigger: false,
        is_override_template: false,
        get_template_id: true,
      },
    });

    const newTemplateId = watch("new_template_id");
    const isNewTemplateChanged = watch("new_template_changed");
    const [isNetSalaryNegative, setIsNetSalaryNegative] = useState(false);
    const {
      newTemplateData,
      setNewTemplateData,
      setTemplateData,
      dataUpdated,
      setDataupdated,
      setHandleButtonLoder,
      setTabValue,
      clearValues,
      setClearValues,
    } = useSalaryRevise();
    const { result: computationMethodChoices } = useFetch(
      `${domain}HRZone/Salary/Salary_Component/get-computation-method-choices/`,
    );

    const { result: earningValue } = useFetch(
      `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Earnings&company_id=${getCompanyId}`,
    );

    const { result: deductionValue } = useFetch(
      `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Deduction&company_id=${getCompanyId}`,
    );

    const { result: employerContributionValue } = useFetch(
      `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Employer Contribution&company_id=${getCompanyId}`,
    );

    const { result: reimbursementValue } = useFetch(
      `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Reimbursement&company_id=${getCompanyId}`,
    );

    const { result: componentCategoryData } = useFetch(
      `${domain}hrzone/salary/salary_template/get-salary-type-choices/`,
    );

    const totalSalary = parseFloat(watch("salary")) || 0;
    const earnings = useSalaryComponents("earnings", earningValue, totalSalary);
    const deductions = useSalaryComponents("deductions", deductionValue);
    const employerContributions = useSalaryComponents(
      "employer_contributions",
      employerContributionValue,
    );
    const reimbursements = useSalaryComponents(
      "reimbursements",
      reimbursementValue,
    );

    const totalEarnings = earnings.totalAmount;
    const totalDeductions = deductions.totalAmount;
    const totalEmployerContributions = employerContributions.totalAmount;
    const totalReimbursements = reimbursements.totalAmount;
    const salary = watch("salary");

    const netSalary = useMemo(() => {
      return (parseFloat(salary) || 0) - (parseFloat(totalDeductions) || 0);
    }, [salary, totalDeductions]);

    const totalCTC = useMemo(
      () => totalEarnings + totalEmployerContributions,
      [totalEarnings, totalEmployerContributions],
    );

    const handleCalType = (event, data) => {
      if (data) {
        setValue("salary_cal_type", data?.label);
      } else {
        setValue("salary_cal_type", "");
      }
    };

    const handleNewSalaryTemplate = (event, data) => {
      if (data) {
        setValue("new_template", data?.template_name);
        setValue("new_template_id", data?.id);
        setValue("new_template_changed", true);
      }
    };

    const handleAmount = (event) => {
      if (
        event.target.value?.length <= 8 &&
        onlyNumberRegex.test(event.target.value)
      ) {
        const newSalary = event.target.value;
        setValue("salary", newSalary);
        setTimeout(() => {
          if (earnings.components.length > 0) {
            const grossComponent = {
              code: "GROSS",
              component_name: { code: "GROSS" },
              amount: parseFloat(newSalary) || 0,
              computation_method: "Fixed Amount",
            };

            const allComponentsMap = {
              earnings: earnings.components,
              deductions: deductions.components,
              employer_contributions: employerContributions.components,
              reimbursements: reimbursements.components,
            };

            const updatedCategories = updateCrossCategoryComponents(
              grossComponent,
              allComponentsMap,
              parseFloat(newSalary) || 0,
            );
            if (updatedCategories.earnings) {
              earnings.setComponents(updatedCategories.earnings);
            }
            if (updatedCategories.deductions) {
              deductions.setComponents(updatedCategories.deductions);
            }
            if (updatedCategories.employer_contributions) {
              employerContributions.setComponents(
                updatedCategories.employer_contributions,
              );
            }
            if (updatedCategories.reimbursements) {
              reimbursements.setComponents(updatedCategories.reimbursements);
            }
          }
        }, 100);
      } else {
        setValue("salary", "");
      }
    };

    const handleEarningsChangeWithRecalc = useCallback(
      (index, type, value) => {
        if (type === "name") {
          earnings.handleComponentNameChange(
            index,
            value,
            earnings.setComponents,
          );
        } else if (type === "method") {
          earnings.handleComputationMethodChange(
            index,
            value,
            earnings.setComponents,
          );
        } else if (type === "amount") {
          earnings.handleAmountChange(index, value, earnings.setComponents);
        }

        setTimeout(() => {
          const changedComponent = earnings.components[index];
          if (changedComponent) {
            const allComponentsMap = {
              earnings: earnings.components,
              deductions: deductions.components,
              employer_contributions: employerContributions.components,
              reimbursements: reimbursements.components,
            };

            const updatedCategories = updateCrossCategoryComponents(
              changedComponent,
              allComponentsMap,
              totalSalary,
            );

            if (updatedCategories.deductions) {
              deductions.setComponents(updatedCategories.deductions);
            }
            if (updatedCategories.employer_contributions) {
              employerContributions.setComponents(
                updatedCategories.employer_contributions,
              );
            }
            if (updatedCategories.reimbursements) {
              reimbursements.setComponents(updatedCategories.reimbursements);
            }
            if (updatedCategories.earnings) {
              earnings.setComponents(updatedCategories.earnings);
            }
          }
        }, 0);
      },
      [
        earnings,
        deductions,
        employerContributions,
        reimbursements,
        totalSalary,
      ],
    );

    const handleDeductionsChangeWithRecalc = useCallback(
      (index, type, value) => {
        if (type === "name") {
          deductions.handleComponentNameChange(
            index,
            value,
            deductions.setComponents,
          );
        } else if (type === "method") {
          deductions.handleComputationMethodChange(
            index,
            value,
            deductions.setComponents,
          );
        } else if (type === "amount") {
          deductions.handleAmountChange(index, value, deductions.setComponents);
        }

        setTimeout(() => {
          const changedComponent = deductions.components[index];
          if (changedComponent) {
            const allComponentsMap = {
              earnings: earnings.components,
              deductions: deductions.components,
              employer_contributions: employerContributions.components,
              reimbursements: reimbursements.components,
            };

            const updatedCategories = updateCrossCategoryComponents(
              changedComponent,
              allComponentsMap,
              totalSalary,
            );

            if (updatedCategories.earnings) {
              earnings.setComponents(updatedCategories.earnings);
            }
            if (updatedCategories.employer_contributions) {
              employerContributions.setComponents(
                updatedCategories.employer_contributions,
              );
            }
            if (updatedCategories.reimbursements) {
              reimbursements.setComponents(updatedCategories.reimbursements);
            }
          }
        }, 0);
      },
      [
        deductions,
        earnings,
        employerContributions,
        reimbursements,
        totalSalary,
      ],
    );

    const handleEmployerContributionsChangeWithRecalc = useCallback(
      (index, type, value) => {
        if (type === "name") {
          employerContributions.handleComponentNameChange(
            index,
            value,
            employerContributions.setComponents,
          );
        } else if (type === "method") {
          employerContributions.handleComputationMethodChange(
            index,
            value,
            employerContributions.setComponents,
          );
        } else if (type === "amount") {
          employerContributions.handleAmountChange(
            index,
            value,
            employerContributions.setComponents,
          );
        }

        setTimeout(() => {
          const changedComponent = employerContributions.components[index];
          if (changedComponent) {
            const allComponentsMap = {
              earnings: earnings.components,
              deductions: deductions.components,
              employer_contributions: employerContributions.components,
              reimbursements: reimbursements.components,
            };

            const updatedCategories = updateCrossCategoryComponents(
              changedComponent,
              allComponentsMap,
              totalSalary,
            );

            if (updatedCategories.earnings) {
              earnings.setComponents(updatedCategories.earnings);
            }
            if (updatedCategories.deductions) {
              deductions.setComponents(updatedCategories.deductions);
            }
            if (updatedCategories.reimbursements) {
              reimbursements.setComponents(updatedCategories.reimbursements);
            }
          }
        }, 0);
      },
      [
        employerContributions,
        earnings,
        deductions,
        reimbursements,
        totalSalary,
      ],
    );

    const handleReimbursementsChangeWithRecalc = useCallback(
      (index, type, value) => {
        if (type === "name") {
          reimbursements.handleComponentNameChange(
            index,
            value,
            reimbursements.setComponents,
          );
        } else if (type === "method") {
          reimbursements.handleComputationMethodChange(
            index,
            value,
            reimbursements.setComponents,
          );
        } else if (type === "amount") {
          reimbursements.handleAmountChange(
            index,
            value,
            reimbursements.setComponents,
          );
        }

        setTimeout(() => {
          const changedComponent = reimbursements.components[index];
          if (changedComponent) {
            const allComponentsMap = {
              earnings: earnings.components,
              deductions: deductions.components,
              employer_contributions: employerContributions.components,
              reimbursements: reimbursements.components,
            };

            const updatedCategories = updateCrossCategoryComponents(
              changedComponent,
              allComponentsMap,
              totalSalary,
            );

            if (updatedCategories.earnings) {
              earnings.setComponents(updatedCategories.earnings);
            }
            if (updatedCategories.deductions) {
              deductions.setComponents(updatedCategories.deductions);
            }
            if (updatedCategories.employer_contributions) {
              employerContributions.setComponents(
                updatedCategories.employer_contributions,
              );
            }
          }
        }, 0);
      },
      [
        reimbursements,
        earnings,
        deductions,
        employerContributions,
        totalSalary,
      ],
    );

    const handleRemoveComponentWithRecalc = useCallback(
      (category, id, index) => {
        let removedComponent = null;
        let updatedComponents = [];

        if (category === "earnings") {
          removedComponent = earnings.components[index];
          updatedComponents = earnings.components.filter(
            (item, i) => i !== index,
          );
          const formula = buildSpecialAllowanceFormula(updatedComponents);
          const updatedComponent = updatedComponents.map((item, i) => {
            if (item?.code === "SP") {
              return {
                ...item,
                value_based_formula: formula,
              };
            }
            return item;
          });
          earnings.setComponents(updatedComponent);
        } else if (category === "deductions") {
          removedComponent = deductions.components[index];
          updatedComponents = deductions.components.filter(
            (item, i) => i !== index,
          );
          deductions.setComponents(updatedComponents);
        } else if (category === "employer_contributions") {
          removedComponent = employerContributions.components[index];
          updatedComponents = employerContributions.components.filter(
            (item, i) => i !== index,
          );
          employerContributions.setComponents(updatedComponents);
        } else {
          removedComponent = reimbursements.components[index];
          updatedComponents = reimbursements.components.filter(
            (item, i) => i !== index,
          );
          reimbursements.setComponents(updatedComponents);
        }

        if (removedComponent) {
          setTimeout(() => {
            const allComponentsMap = {
              earnings:
                category === "earnings"
                  ? updatedComponents
                  : earnings.components,
              deductions:
                category === "deductions"
                  ? updatedComponents
                  : deductions.components,
              employer_contributions:
                category === "employer_contributions"
                  ? updatedComponents
                  : employerContributions.components,
              reimbursements:
                category === "reimbursements"
                  ? updatedComponents
                  : reimbursements.components,
            };

            const updatedCategories = updateCrossCategoryComponents(
              removedComponent,
              allComponentsMap,
              totalSalary,
            );

            if (updatedCategories.earnings && category !== "earnings") {
              earnings.setComponents(updatedCategories.earnings);
            }
            if (updatedCategories.deductions && category !== "deductions") {
              deductions.setComponents(updatedCategories.deductions);
            }
            if (
              updatedCategories.employer_contributions &&
              category !== "employer_contributions"
            ) {
              employerContributions.setComponents(
                updatedCategories.employer_contributions,
              );
            }
            if (
              updatedCategories.reimbursements &&
              category !== "reimbursements"
            ) {
              reimbursements.setComponents(updatedCategories.reimbursements);
            }
          }, 0);
        }
      },
      [
        earnings,
        deductions,
        employerContributions,
        reimbursements,
        totalSalary,
      ],
    );

    const handleComponentUpdate = useCallback(
      (category, index, updatedComponent) => {
        let updatedComponents;
        let allComponentsMap;

        const enhancedComponent = updatePercentageDisplay(updatedComponent);

        if (category === "earnings") {
          updatedComponents = [...earnings.components];
          updatedComponents[index] = enhancedComponent;
          earnings.setComponents(updatedComponents);

          allComponentsMap = {
            earnings: updatedComponents,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };
        } else if (category === "deductions") {
          updatedComponents = [...deductions.components];
          updatedComponents[index] = enhancedComponent;
          deductions.setComponents(updatedComponents);

          allComponentsMap = {
            earnings: earnings.components,
            deductions: updatedComponents,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };
        } else if (category === "employer_contributions") {
          updatedComponents = [...employerContributions.components];
          updatedComponents[index] = enhancedComponent;
          employerContributions.setComponents(updatedComponents);

          allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: updatedComponents,
            reimbursements: reimbursements.components,
          };
        } else {
          return;
        }

        setTimeout(() => {
          const updatedCategories = updateCrossCategoryComponents(
            updatedComponent,
            allComponentsMap,
            totalSalary,
          );

          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.deductions) {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (updatedCategories.employer_contributions) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions,
            );
          }
          if (updatedCategories.reimbursements) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
        }, 0);
      },
      [
        earnings,
        deductions,
        employerContributions,
        reimbursements,
        totalSalary,
      ],
    );

    const validateForm = useCallback(async () => {
      const isFormValid = await trigger([
        "salary_cal_type",
        "salary_basis",
        "salary",
        "new_template",
      ]);

      const isEarningsValid = earnings.validateComponents();
      const isDeductionsValid = deductions.validateComponents();
      const isEmployerContributionsValid =
        employerContributions.validateComponents();
      const isReimbursementsValid = reimbursements.validateComponents();

      return (
        isFormValid &&
        isEarningsValid &&
        isDeductionsValid &&
        isEmployerContributionsValid &&
        isReimbursementsValid
      );
    }, [deductions, earnings, employerContributions, reimbursements, trigger]);

    const onSubmit = useCallback(
      async (data) => {
        const isValid = await validateForm();
        if (!isValid) {
          return;
        }
        if (netSalary < 0) {
          setIsNetSalaryNegative(true);
          return;
        }
        const requestData = {
          is_override_template: data?.is_override_template,
          salary: data?.salary,
          total_salary: totalEarnings,
          total_deductions: totalDeductions,
          net_salary: netSalary,
          total_employer_contribution: totalEmployerContributions,
          total_ctc: totalCTC,
          total_reimbursement: totalReimbursements,
          salary_basis: data?.salary_basis,
          salary_type: data?.salary_cal_type,
          new_template: data?.new_template,
          new_template_id: newTemplateId,
          earnings: formatComponents(earnings?.components),
          deduction: formatComponents(deductions?.components),
          employer_contribution: formatComponents(
            employerContributions?.components,
          ),
          reimbursement: formatComponents(reimbursements?.components),
        };
        setTemplateData(requestData);
        setNewTemplateData(requestData);
        setTabValue(1);
        setHandleButtonLoder(false);
      },
      [
        deductions?.components,
        earnings?.components,
        employerContributions?.components,
        netSalary,
        newTemplateId,
        reimbursements?.components,
        setHandleButtonLoder,
        setNewTemplateData,
        setTabValue,
        setTemplateData,
        totalCTC,
        totalDeductions,
        totalEarnings,
        totalEmployerContributions,
        totalReimbursements,
        validateForm,
      ],
    );

    useImperativeHandle(ref, () => ({
      submit: () => handleSubmit(onSubmit)(),
    }));

    useEffect(() => {
      if (dataUpdated && newTemplateData?.length !== 0) {
        setValue(
          "new_template",
          newTemplateData?.template_name ?? newTemplateData?.new_template,
        );
        setValue(
          "new_template_id",
          newTemplateData?.new_template_id ?? newTemplateData?.id,
        );
        setValue("salary_cal_type", newTemplateData?.salary_type);
        setValue("salary_basis", newTemplateData?.salary_basis);
        setValue("salary", newTemplateData?.salary);
        setValue(
          "is_override_template",
          newTemplateData?.is_override_template ?? newTemplateData?.over_ride,
        );
        if (newTemplateData.earnings?.length > 0) {
          const earningsData = newTemplateData.earnings.map((earning) => {
            const isPercentage = earning?.computation_method === "Percentage";
            const parsedPercentage = isPercentage
              ? parsePercentageValue(earning?.computation_value)
              : {};

            return {
              id: earning?.component_id,
              component_name: {
                label: earning?.component_name,
                id: earning?.component_id,
                code: earning?.component_code,
              },
              component_category: "Earnings",
              computation_method: earning?.computation_method,
              amount: earning?.amount || 0,
              code: earning?.component_code,
              isSpecialAllowance:
                earning?.component_code === "SP" &&
                earning?.component_name === "Special Allowance",
              isBasic: earning?.component_code === "BASIC",
              computation_value: isPercentage
                ? parsedPercentage.computation_value
                : earning?.computation_value,
              value_based_on: isPercentage
                ? parsedPercentage.value_based_on
                : earning?.value_based_on,
              value_based_formula: earning?.computation_value,
              ...(isPercentage && {
                value_based_percentage: earning?.computation_value,
              }),
            };
          });

          earnings.setComponents(earningsData);
        } else {
          earnings.setComponents([]);
        }

        // Deductions
        if (newTemplateData.deduction?.length > 0) {
          deductions.setComponents(
            newTemplateData.deduction.map((deduction) => {
              const isPercentage =
                deduction?.computation_method === "Percentage";

              const parsedPercentage = isPercentage
                ? parsePercentageValue(deduction?.computation_value)
                : {};

              return {
                id: deduction?.component_id,
                component_name: {
                  label: deduction?.component_name,
                  id: deduction?.component_id,
                  code: deduction?.component_code,
                },
                component_category: "Deduction",
                computation_method: deduction?.computation_method,
                amount: deduction?.amount || 0,
                code: deduction?.component_code,
                computation_value: isPercentage
                  ? parsedPercentage.computation_value
                  : deduction?.computation_value,
                value_based_on: isPercentage
                  ? parsedPercentage.value_based_on
                  : deduction?.value_based_on,
                value_based_formula: deduction?.computation_value,
                ...(isPercentage && {
                  value_based_percentage: deduction?.computation_value,
                }),
              };
            }),
          );
        } else {
          deductions.setComponents([]);
        }

        // Employer Contribution
        if (newTemplateData.employer_contribution?.length > 0) {
          employerContributions.setComponents(
            newTemplateData.employer_contribution.map((contribution) => {
              const isPercentage =
                contribution?.computation_method === "Percentage";

              const parsedPercentage = isPercentage
                ? parsePercentageValue(contribution?.computation_value)
                : {};

              return {
                id: contribution?.component_id,
                component_name: {
                  label: contribution?.component_name,
                  id: contribution?.component_id,
                  code: contribution?.component_code,
                },
                component_category: "Employer Contribution",
                computation_method: contribution?.computation_method,
                amount: contribution?.amount || 0,
                code: contribution?.component_code,
                computation_value: isPercentage
                  ? parsedPercentage.computation_value
                  : contribution?.computation_value,
                value_based_on: isPercentage
                  ? parsedPercentage.value_based_on
                  : contribution?.value_based_on,
                value_based_formula: contribution?.computation_value,
                ...(isPercentage && {
                  value_based_percentage: contribution?.computation_value,
                }),
              };
            }),
          );
        } else {
          employerContributions.setComponents([]);
        }

        // Reimbursement
        if (newTemplateData.reimbursement?.length > 0) {
          reimbursements.setComponents(
            newTemplateData.reimbursement.map((reimbursement) => ({
              id: reimbursement?.component_id,
              component_name: {
                label: reimbursement?.component_name,
                id: reimbursement?.component_id,
                code: reimbursement?.component_code,
              },
              component_category: "Reimbursement",
              computation_method: reimbursement?.computation_method,
              amount: reimbursement?.amount || 0,
              code: reimbursement?.component_code,
              computation_value: reimbursement?.computation_value,
              value_based_on: reimbursement?.value_based_on,
              value_based_formula: reimbursement?.value_based_formula,
            })),
          );
        } else {
          reimbursements.setComponents([]);
        }
        setDataupdated(false);
      } else if (clearValues && newTemplateData?.length === 0) {
        setValue("new_template", "");
        setValue("new_template_id", "");
        setValue("salary_cal_type", "");
        setValue("salary_basis", "");
        setValue("salary", "");
        setValue("is_override_template", "");
        earnings.setComponents([]);
        deductions.setComponents([]);
        employerContributions.setComponents([]);
        reimbursements.setComponents([]);
        setDataupdated(false);
        setClearValues(false);
      }
    }, [
      clearValues,
      dataUpdated,
      deductions,
      earnings,
      employerContributions,
      newTemplateData,
      reimbursements,
      revisionBasedOn,
      setClearValues,
      setDataupdated,
      setValue,
    ]);

    useEffect(() => {
      const getNewTemplateDetails = async () => {
        const res = await customGetAPI(
          `${domain}hrzone/salary/salary_template/salary-template-list/?template_id=${watch(
            "new_template_id",
          )}`,
        );

        if (res.ok) {
          setNewTemplateData(res?.responseDetails);
          setValue("new_template_changed", false);
          setDataupdated(true);
        } else {
          showToast("error", res?.message);
        }
      };

      if (newTemplateId && isNewTemplateChanged) {
        getNewTemplateDetails();
      }
    }, [
      newTemplateId,
      isNewTemplateChanged,
      domain,
      watch,
      setValue,
      setNewTemplateData,
      setDataupdated,
    ]);

    return (
      <Box
        sx={{
          mt: 2,
          borderRadius: 2,
          padding: 2,
        }}
      >
        <Box>
          <Box sx={{ ...childBoxStyleSpaceBetween, px: 0, borderRadius: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 20 }}>
              Override Template
            </Typography>
            <Switch
              checked={watch("is_override_template")}
              onChange={(event) => {
                const checked = event.target.checked;
                setValue("is_override_template", checked);
              }}
              sx={{ transform: "scale(1.3)" }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            pointerEvents: !watch("is_override_template") ? "none" : "auto",
            opacity: !watch("is_override_template") ? 0.8 : 1,
            ...childBoxStyle,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                revisionBasedOn === "Template" ? "1fr" : "1fr 1fr",
              gap: 2,
              mb: 2,
            }}
          >
            {revisionBasedOn !== "Template" && (
              <Box
                sx={{
                  pointerEvents:
                    revisionBasedOn === "Employee" ? "auto" : "none",
                  opacity: revisionBasedOn === "Employee" ? 1 : 0.6,
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>
                  New Salary Template
                </Typography>
                <Typography {...infoStyle}>
                  Choose the type of salary structure.
                </Typography>
                <CustomAutoComplete
                  dataList={salaryTemplates
                    .filter((obj) => obj.template_name !== templateName)
                    .map((obj) => ({
                      ...obj,
                      label: obj.template_name,
                    }))}
                  rules={{
                    ...register("new_template", {
                      required: "New Salary template is required",
                    }),
                  }}
                  selectedValue={watch("new_template")}
                  updateDataFunction={handleNewSalaryTemplate}
                  placeHolder="Select new salary template"
                  errorMessage={
                    errors?.new_template?.message
                      ? errors?.new_template?.message
                      : ""
                  }
                  textBoxError={errors?.new_template ? true : false}
                />
              </Box>
            )}
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Salary Calculation Type
              </Typography>
              <Typography {...infoStyle}>
                Choose the type of salary structure.
              </Typography>
              <CustomAutoComplete
                dataList={componentCategoryData}
                rules={{
                  ...register("salary_cal_type", {
                    required: "Salary calculation type is required",
                  }),
                }}
                selectedValue={watch("salary_cal_type")}
                updateDataFunction={handleCalType}
                placeHolder="Select salary calculation type"
                errorMessage={
                  errors?.salary_cal_type?.message
                    ? errors?.salary_cal_type?.message
                    : ""
                }
                textBoxError={errors?.salary_cal_type ? true : false}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Salary Basis</Typography>
              <Typography {...infoStyle}>
                Enter the employee’s fixed gross salary amount.
              </Typography>
              <CustomTextBox
                selectedValue={watch("salary_basis")}
                disabledValue={true}
              />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 600 }}>Amount</Typography>
              <Typography {...infoStyle}>
                Enter the employee’s fixed gross salary amount.
              </Typography>
              <CustomTextBox
                selectedValue={watch("salary")}
                rules={{
                  ...register("salary", {
                    required: "Salary is required",
                    validate: (value) => {
                      if (value && parseFloat(value) <= 0) {
                        return "Salary must be greater than 0";
                      }
                      return true;
                    },
                  }),
                }}
                updateDataFunction={handleAmount}
                placeHolder="Enter amount"
                errorMessage={
                  errors?.salary?.message ? errors?.salary?.message : ""
                }
                textBoxError={errors.salary ? true : false}
              />
            </Box>
          </Box>

          <SalaryComponentTable
            title="Earnings"
            description="Add components that contribute to employee earnings (Basic, HRA, Bonus, etc.)"
            data={earnings.components}
            earningValue={[
              ...earnings?.components.slice(0, -1),
              { label: "GROSS", code: "GROSS", amount: totalSalary },
            ]}
            is_earning={true}
            setData={earnings.setComponents}
            errors={earnings.errors}
            setErrors={earnings.setErrors}
            componentList={earningValue?.data || []}
            computationMethodChoices={computationMethodChoices || []}
            filteredComponentLists={earnings.filteredComponentLists}
            totalLabel="Total Earnings"
            totalSalary={totalSalary}
            onAddComponent={() => earnings.addComponent()}
            onRemoveComponent={(id, index) =>
              handleRemoveComponentWithRecalc("earnings", id, index)
            }
            onComponentNameChange={(index, value) =>
              handleEarningsChangeWithRecalc(index, "name", value)
            }
            onComputationMethodChange={(index, value) =>
              handleEarningsChangeWithRecalc(index, "method", value)
            }
            onAmountChange={(index, event) =>
              handleEarningsChangeWithRecalc(index, "amount", event)
            }
            onComponentUpdate={(index, updatedComponent) =>
              handleComponentUpdate("earnings", index, updatedComponent)
            }
          />

          <Divider />

          <SalaryComponentTable
            title="Deductions"
            description="Add components that are deducted from earnings (TDS, PF, Loan, etc.)"
            data={deductions.components}
            earningValue={[
              ...earnings?.components,
              { label: "GROSS", code: "GROSS", amount: totalSalary },
            ]}
            setData={deductions.setComponents}
            errors={deductions.errors}
            setErrors={deductions.setErrors}
            componentList={deductionValue?.data || []}
            computationMethodChoices={computationMethodChoices || []}
            filteredComponentLists={deductions.filteredComponentLists}
            totalLabel="Total Deductions"
            totalSalary={totalSalary}
            onAddComponent={() => deductions.addComponent()}
            onRemoveComponent={(id, index) =>
              handleRemoveComponentWithRecalc("deductions", id, index)
            }
            onComponentNameChange={(index, value) =>
              handleDeductionsChangeWithRecalc(index, "name", value)
            }
            onComputationMethodChange={(index, value) =>
              handleDeductionsChangeWithRecalc(index, "method", value)
            }
            onAmountChange={(index, event) =>
              handleDeductionsChangeWithRecalc(index, "amount", event)
            }
            onComponentUpdate={(index, updatedComponent) =>
              handleComponentUpdate("deductions", index, updatedComponent)
            }
          />

          <Divider />

          <SalaryComponentTable
            title="Employer Contribution"
            description="Add statutory or custom employer contributions (PF,ESI,Insurance,etc."
            data={employerContributions.components}
            earningValue={[
              ...earnings?.components,
              { label: "GROSS", code: "GROSS", amount: totalSalary },
            ]}
            setData={employerContributions.setComponents}
            errors={employerContributions.errors}
            setErrors={employerContributions.setErrors}
            componentList={employerContributionValue?.data || []}
            computationMethodChoices={computationMethodChoices || []}
            filteredComponentLists={
              employerContributions.filteredComponentLists
            }
            totalLabel="Total Contribution"
            totalSalary={totalSalary}
            onAddComponent={() => employerContributions.addComponent()}
            onRemoveComponent={(id, index) =>
              handleRemoveComponentWithRecalc(
                "employer_contributions",
                id,
                index,
              )
            }
            onComponentNameChange={(index, value) =>
              handleEmployerContributionsChangeWithRecalc(index, "name", value)
            }
            onComputationMethodChange={(index, value) =>
              handleEmployerContributionsChangeWithRecalc(
                index,
                "method",
                value,
              )
            }
            onAmountChange={(index, event) =>
              handleEmployerContributionsChangeWithRecalc(
                index,
                "amount",
                event,
              )
            }
            onComponentUpdate={(index, updatedComponent) =>
              handleComponentUpdate(
                "employer_contributions",
                index,
                updatedComponent,
              )
            }
          />

          <Divider />

          <SalaryComponentTable
            title="Reimbursement"
            description="Add reimbursement components for employee expenses (Travel,Food,Medical, etc.)"
            data={reimbursements.components}
            earningValue={[
              ...earnings?.components,
              { label: "GROSS", code: "GROSS", amount: totalSalary },
            ]}
            setData={reimbursements.setComponents}
            errors={reimbursements.errors}
            setErrors={reimbursements.setErrors}
            componentList={reimbursementValue?.data || []}
            computationMethodChoices={(computationMethodChoices || []).filter(
              (item) => item.label === "Fixed Amount",
            )}
            filteredComponentLists={reimbursements.filteredComponentLists}
            totalLabel="Total Reimbursement"
            onAddComponent={() => reimbursements.addComponent()}
            onRemoveComponent={(id, index) =>
              handleRemoveComponentWithRecalc("reimbursements", id, index)
            }
            onComponentNameChange={(index, value) =>
              handleReimbursementsChangeWithRecalc(index, "name", value)
            }
            onComputationMethodChange={(index, value) =>
              handleReimbursementsChangeWithRecalc(index, "method", value)
            }
            onAmountChange={(index, event) =>
              handleReimbursementsChangeWithRecalc(index, "amount", event)
            }
            onComponentUpdate={(index, updatedComponent) =>
              handleComponentUpdate("reimbursements", index, updatedComponent)
            }
          />

          <SummaryData
            totalCTC={totalCTC}
            totalEmployerContributions={totalEmployerContributions}
            totalEarnings={totalEarnings}
            totalDeductions={totalDeductions}
            netSalary={netSalary}
            isNetSalaryNegative={isNetSalaryNegative}
          />
        </Box>
      </Box>
    );
  },
);
